import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { interval } from 'rxjs';
import { finalize } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { Notifications } from 'src/app/models/IMessage.model';
import { IWsResult } from 'src/app/models/ws_result.model';
import { MessagesService } from 'src/app/services/messages.service';
import { SessionService } from 'src/app/services/shared/session.service';
import { Log } from 'src/app/services/shared/log';

/**
 * Sidebar grouping mode.
 *
 *   `contacts` — one row per person the admin has exchanged messages with.
 *                Backend: `getConversations` + `getConversation(sender_id)`.
 *   `orders`   — one row per order that has any message attached.
 *                Backend: `getConversationsGroup` + `getConversationGroup(order_id)`.
 *
 * Stored in the toggle at the top of the sidebar.
 */
type SidebarMode = 'contacts' | 'orders';

/**
 * Chat page (route `/messaggi`).
 *
 * Single-component replacement for the four legacy components that used to
 * live under `messaggi/*` (list / dettagli / new) plus the old `chat` route.
 * Implements a WhatsApp-style two-column layout — sidebar with the list of
 * conversations on the left, the active conversation with the message
 * stream + input on the right. Toggle on the sidebar header switches
 * between grouping by *contact* and grouping by *order*; the two backends
 * are already there (`getConversations` vs `getConversationsGroup`).
 *
 * Design language is shared with the dashboard / statistics / settings
 * redesigns (palette, sticky header, accent strips); see the SCSS.
 *
 * Polling: every `REFRESH_INTERVAL_MS` we re-fetch the sidebar list
 * + the messages of the active conversation. Optimistic add on send.
 *
 * Audience: admin / super-admin (operators don't open this page — they
 * write/read messages from the order detail screen, which uses the same
 * `MessagesService`).
 *
 * Architectural notes / Phase 2 candidates:
 *  - The model `IConversationMessage` has no per-message `ms_status`,
 *    so the read tick (✓✓ blue) is rendered at the *conversation* level,
 *    on the last message only. Per-message tick requires a backend
 *    addition (`ms_status` on `IConversationMessage` + a way to mark a
 *    single message read). Documented in CHANGELOG.
 *  - When the codebase moves to signals, the imperative subscribes
 *    become `toSignal()` calls and the polling becomes an RxJS pipeline
 *    keyed off a `timer` signal.
 *  - `WonderPush` integration was stripped here (it was dead code: the
 *    consent flow + the subscribe path were defined but never invoked).
 *    A future re-enable can ship as its own feature.
 */
@Component({
  selector: 'app-chat',
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss'],
  standalone: false,
})


export class ChatComponent implements OnInit, AfterViewChecked {

  private readonly messagesService = inject(MessagesService);
  private readonly session = inject(SessionService);
  private readonly destroyRef = inject(DestroyRef);
  // ChangeDetectorRef is injected (and used after every async state
  // mutation) because the page lives behind an HTTP layer whose
  // responses, on this specific route, don't reliably re-enter the
  // Angular zone — without explicit `markForCheck()` the bubble list
  // and the sidebar stay in their "loading" state until any user
  // interaction (e.g. typing into the search) forces a fresh CD tick.
  // The previous (pre-v2.3.0) component used `detectChanges()` for the
  // same reason. `markForCheck()` is the gentler equivalent.
  private readonly cdr = inject(ChangeDetectorRef);

  /** Poll the active conversation + the sidebar every N ms. */
  // 🚨 v2.11.2 (Q7): ridotto da 20s a 8s. Per una chat "live" è il
  // compromesso tra fresh visivo e load BE accettabile.
  private static readonly REFRESH_INTERVAL_MS = 8_000;

  /** Element used to keep the message stream scrolled to bottom. */
  @ViewChild('messagesScroll') private messagesScrollEl?: ElementRef<HTMLElement>;

  /** `true` while we still owe a "scroll to bottom" to the DOM. */
  private pendingScrollToBottom = false;

  // ── Sidebar state ─────────────────────────────────────────────────
  mode: SidebarMode = 'contacts';
  conversations: Notifications.Conversations[] = [];
  searchText = '';
  loadingSidebar = false;

  // ── Active conversation state ─────────────────────────────────────
  selectedConversation: Notifications.Conversations | null = null;
  selectedMessages: Notifications.IConversationMessage[] = [];
  loadingMessages = false;

  // ── Composer state ────────────────────────────────────────────────
  draft = '';
  sending = false;

  // ── Identity (cached at boot) ─────────────────────────────────────
  private currentUserId = 0;

  ngOnInit(): void {
    const user = this.session.getCurrentUser();
    this.currentUserId = Number(user?.['account_id'] ?? 0);

    this.loadSidebar();
    this.startPolling();
  }

  ngAfterViewChecked(): void {
    // The scroll lives in a child element whose height changes after
    // each render. We scroll-to-bottom only when we know new content has
    // arrived (set `pendingScrollToBottom = true` when we accept new
    // messages), and we do it here so the DOM is already laid out.
    if (this.pendingScrollToBottom) {
      this.pendingScrollToBottom = false;
      this.scrollToBottom();
    }
  }

  // ──────────────────────────────────────────────────────────────────
  // Derived state — kept as getters so the template stays declarative.
  // ──────────────────────────────────────────────────────────────────

  /** Sidebar filtered by `searchText` (matches name or order code). */
  get filteredConversations(): Notifications.Conversations[] {
    const q = this.searchText.trim().toLowerCase();
    if (!q) return this.conversations;
    return this.conversations.filter((c) => this.haystack(c).includes(q));
  }

  /** Title shown in the chat header (name in `contacts` mode, order code in `orders`). */
  get activeTitle(): string {
    if (!this.selectedConversation) return '';
    return this.mode === 'orders'
      ? this.orderLabel(this.selectedConversation)
      : this.contactLabel(this.selectedConversation);
  }

  /** Subtitle shown under the chat header. */
  get activeSubtitle(): string {
    if (!this.selectedConversation) return '';
    return this.mode === 'orders'
      ? this.contactLabel(this.selectedConversation)
      : `Ultimo aggiornamento: ${this.formatDate(this.selectedConversation.ms_datetime)}`;
  }

  /** True when the message stream contains at least one in-flight bubble. */
  get hasMessages(): boolean {
    return this.selectedMessages.length > 0;
  }

  // ──────────────────────────────────────────────────────────────────
  // Template helpers.
  // ──────────────────────────────────────────────────────────────────

  /** `sent` if message is from the current user, `received` otherwise. */
  bubbleSide(msg: Notifications.IConversationMessage): 'sent' | 'received' {
    return msg.ms_sender_id === this.currentUserId ? 'sent' : 'received';
  }

  /** Display label for a conversation in `contacts` mode. */
  contactLabel(c: Notifications.Conversations): string {
    return c.ms_sender_name || c.ms_recipient_name || '—';
  }

  /** Display label for a conversation in `orders` mode. */
  orderLabel(c: Notifications.Conversations): string {
    return c.ms_order_code ? `Ordine ${c.ms_order_code}` : `Ordine #${c.order_id ?? '—'}`;
  }

  /** Avatar initials (up to 2 chars) for the sidebar item. */
  initials(c: Notifications.Conversations): string {
    const label =
      this.mode === 'orders'
        ? c.ms_order_code || String(c.order_id ?? '')
        : this.contactLabel(c);
    const parts = label.split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }

  /** True if the conversation has unread messages (used by the sidebar accent). */
  isUnread(c: Notifications.Conversations): boolean {
    return c.ms_status === 'U';
  }

  /** True if the LAST message of the active conversation has been read. */
  get lastMessageReadByPeer(): boolean {
    return this.selectedConversation?.ms_status === 'R';
  }

  /** ISO datetime → "HH:mm" for inline message timestamps. */
  formatTime(iso: string | undefined | null): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  }

  /**
   * 🚨 v2.12.0 (L.4): label per il date-separator WhatsApp-style tra
   * messaggi di giorni diversi.
   *   • Oggi              → "Oggi"
   *   • Ieri              → "Ieri"
   *   • Ultimi 6 giorni   → "Giovedì" (nome giorno della settimana)
   *   • Più vecchio       → "dd/MM/yyyy"
   */
  dayLabel(iso: string | undefined | null): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(d);
    target.setHours(0, 0, 0, 0);

    const diffDays = Math.round((today.getTime() - target.getTime()) / 86_400_000);

    if (diffDays === 0) return 'Oggi';
    if (diffDays === 1) return 'Ieri';
    if (diffDays > 1 && diffDays < 7) {
      const weekday = d.toLocaleDateString('it-IT', { weekday: 'long' });
      // Capitalize: "lunedì" → "Lunedì"
      return weekday.charAt(0).toUpperCase() + weekday.slice(1);
    }
    return d.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }

  /**
   * 🚨 v2.12.0 (L.4): true se il messaggio corrente è di un giorno
   * diverso rispetto al precedente. Il caller (template) lo usa per
   * decidere se mostrare il date-separator sopra la bubble. Sempre
   * true per il primo messaggio (idx 0 → null prev).
   */
  showDateSeparator(idx: number): boolean {
    if (idx === 0) return true;
    const curr = this.selectedMessages[idx]?.ms_datetime;
    const prev = this.selectedMessages[idx - 1]?.ms_datetime;
    if (!curr || !prev) return false;
    const a = new Date(curr);
    const b = new Date(prev);
    if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return false;
    return (
      a.getFullYear() !== b.getFullYear() ||
      a.getMonth() !== b.getMonth() ||
      a.getDate() !== b.getDate()
    );
  }

  /** ISO datetime → "dd/MM HH:mm" for the sidebar / chat header subtitle. */
  formatDate(iso: string | undefined | null): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  // ──────────────────────────────────────────────────────────────────
  // Actions.
  // ──────────────────────────────────────────────────────────────────

  /** Switch sidebar grouping and reset the active conversation. */
  setMode(next: SidebarMode): void {
    if (this.mode === next) return;
    this.mode = next;
    this.selectedConversation = null;
    this.selectedMessages = [];
    this.searchText = '';
    this.loadSidebar();
  }

  /** Open a conversation: load its messages + auto-mark-as-read. */
  open(conv: Notifications.Conversations): void {
    if (this.loadingMessages) return;
    this.selectedConversation = conv;
    this.selectedMessages = [];
    this.loadMessages(conv);
    this.markConversationRead(conv);
  }

  /** Send the draft as a new message in the active conversation. */
  send(): void {
    if (this.sending || !this.selectedConversation) return;
    const text = this.draft.trim();
    if (!text) return;

    const conv = this.selectedConversation;
    const optimistic: Notifications.IConversationMessage = {
      ms_title: text,
      ms_message: text,
      ms_sender_id: this.currentUserId,
      ms_recipient_id: conv.ms_recipient_id ?? conv.ms_sender_id,
      ms_order_id: conv.order_id,
      ms_datetime: new Date().toISOString(),
    };

    // Optimistic insert — operator sees the bubble immediately.
    this.selectedMessages = [...this.selectedMessages, optimistic];
    this.draft = '';
    this.pendingScrollToBottom = true;
    this.sending = true;

    const payload: Notifications.IMessage = {
      ms_title: optimistic.ms_title,
      ms_message: optimistic.ms_message,
      ms_recipient_id: optimistic.ms_recipient_id,
      ms_order_id: optimistic.ms_order_id ?? null,
    };

    this.messagesService
      .sendNotification(payload)
      .pipe(
        finalize(() => {
          this.sending = false;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (result) => {
          const ok = (result as IWsResult)?.success;
          if (!ok) this.rollbackOptimistic(optimistic, 'Il messaggio non è stato inviato.');
        },
        error: (err) => {
          Log.error('chat: send failed', err);
          this.rollbackOptimistic(optimistic, 'Errore di rete. Riprova.');
        },
      });
  }

  // ──────────────────────────────────────────────────────────────────
  // Data loaders.
  // ──────────────────────────────────────────────────────────────────

  /** Reload sidebar list based on the current `mode`. */
  private loadSidebar(): void {
    this.loadingSidebar = true;
    const stream$ =
      this.mode === 'orders'
        ? this.messagesService.getConversationsGroup()
        : this.messagesService.getConversations();

    stream$
      .pipe(
        finalize(() => {
          this.loadingSidebar = false;
          // 🚨 FIX v2.11.2 (Q7): markForCheck non basta in alcuni contesti
          // (osservabili che emettono fuori dalla zona o casi edge di CD
          // non-OnPush). detectChanges forza render sincrono → la sidebar
          // appare subito al primo load senza necessità di click.
          this.cdr.detectChanges();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (data) => {
          this.conversations = data ?? [];
          this.cdr.detectChanges();
        },
        error: (err) => Log.error('chat: load sidebar failed', err),
      });
  }

  /** Reload messages of the given conversation. */
  private loadMessages(conv: Notifications.Conversations): void {
    // Pick the routing parameter for the right endpoint. The backend
    // payload for the "orders" mode sometimes carries the order id
    // under `order_id`, sometimes under `ms_order_id` (different
    // queries on the same table) — we accept both.
    const lookupId =
      this.mode === 'orders'
        ? (conv.order_id ?? (conv as unknown as { ms_order_id?: number }).ms_order_id ?? 0)
        : (conv.ms_sender_id ?? 0);

    if (!lookupId) {
      Log.warn(
        `chat: cannot load messages for mode=${this.mode} — ` +
          `missing identifier on conversation (message_id=${conv.message_id}).`,
      );
      this.loadingMessages = false;
      this.selectedMessages = [];
      this.cdr.markForCheck();
      return;
    }

    this.loadingMessages = true;
    const stream$ =
      this.mode === 'orders'
        ? this.messagesService.getConversationGroup(lookupId)
        : this.messagesService.getConversation(lookupId);

    stream$
      .pipe(
        finalize(() => {
          this.loadingMessages = false;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (result: IWsResult) => {
          if (!result?.success) {
            this.selectedMessages = [];
            this.cdr.markForCheck();
            return;
          }
          // `getConversationGroup` returns an array of messages directly,
          // `getConversation` returns an IConversation { messages: [...] }.
          const data = result.data;
          this.selectedMessages = Array.isArray(data)
            ? (data as Notifications.IConversationMessage[])
            : ((data?.messages as Notifications.IConversationMessage[]) ?? []);
          this.pendingScrollToBottom = true;
          this.cdr.markForCheck();
        },
        error: (err) => Log.error('chat: load messages failed', err),
      });
  }

  /**
   * If the conversation is currently flagged unread (`ms_status === 'U'`),
   * call `setReadStatus` on the latest message id and update the local
   * model optimistically so the sidebar accent disappears immediately.
   */
  private markConversationRead(conv: Notifications.Conversations): void {
    if (conv.ms_status !== 'U') return;
    if (!conv.message_id) return;

    this.messagesService
      .setReadStatus(conv.message_id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          conv.ms_status = 'R';
          this.cdr.markForCheck();
        },
        error: (err) => Log.error('chat: setReadStatus failed', err),
      });
  }

  /**
   * Background polling: refresh sidebar + active conversation.
   *
   * 🚨 FIX v2.11.2 (Q7): legacy emetteva solo dopo REFRESH_INTERVAL_MS
   * (20s) → primo refresh dopo 20s + gap 20s tra refresh successivi
   * (troppo per UX chat "live", l'utente non vedeva messaggi nuovi
   * timely). Ridotto a 8s per dare freshness percepita più chat-like
   * senza saturare il BE. L'init iniziale è già coperto dalla
   * `loadSidebar()` esplicita in ngOnInit, quindi nessun startWith
   * serve qui.
   */
  private startPolling(): void {
    interval(ChatComponent.REFRESH_INTERVAL_MS)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.loadSidebar();
        if (this.selectedConversation) this.loadMessages(this.selectedConversation);
      });
  }

  // ──────────────────────────────────────────────────────────────────
  // Private helpers.
  // ──────────────────────────────────────────────────────────────────

  /** Build the search "haystack" for sidebar filtering. */
  private haystack(c: Notifications.Conversations): string {
    return [
      c.ms_sender_name,
      c.ms_recipient_name,
      c.ms_order_code,
      String(c.order_id ?? ''),
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
  }

  /** Roll back an optimistically-added message after a failed send. */
  private rollbackOptimistic(
    optimistic: Notifications.IConversationMessage,
    userMessage: string,
  ): void {
    this.selectedMessages = this.selectedMessages.filter((m) => m !== optimistic);
    this.draft = optimistic.ms_message;
    Swal.fire({
      icon: 'error',
      title: 'Invio non riuscito',
      text: userMessage,
      timer: 2200,
      timerProgressBar: true,
    });
  }

  /** Scroll the message stream to the bottom. */
  private scrollToBottom(): void {
    const el = this.messagesScrollEl?.nativeElement;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }
  
}
